/**
 * NutrientField — pellets orbiting the obstacle shards.
 *
 * Each pellet is bound to one shard and circles it in a ring band.
 * That makes feeding a navigation puzzle: weave in close to the rock
 * to absorb the orbit without actually colliding with the shard. An
 * earlier version had pellets wandering inside random "patches" with
 * no central hazard; orbits + obstacles is the upgrade that makes
 * the world feel like a real obstacle course.
 *
 * Per-pellet orbit speeds and small radial wobble keep the rings
 * from looking mechanical — pellets jitter slightly around their
 * nominal radius so a ring reads as a swarm orbiting a rock, not
 * a clockwork dial.
 */

import { Container, Graphics } from 'pixi.js';
import type { Obstacle } from './obstacles';

interface Nutrient {
  /** World position. Recomputed every frame from orbit params. */
  x: number;
  y: number;
  /** Index into the obstacles array — the shard this pellet orbits. */
  obstacle: number;
  /** Angle around the shard, radians. */
  orbitAngle: number;
  /** Angular velocity, rad/s. Sign sets direction (some CW, some CCW). */
  orbitSpeed: number;
  /** Nominal orbit radius from the shard center, world px. */
  orbitRadius: number;
  /** Per-pellet radial wobble — small breathing in/out around radius. */
  wobbleAmp: number;
  wobblePhase: number;
  wobbleFreq: number;
  /** Cosmetic scale pulse. */
  pulsePhase: number;
  pulseFreq: number;
  alive: boolean;
  /** Seconds until respawn (only meaningful while !alive). */
  respawnIn: number;
  g: Graphics;
}

const RADIUS = 5;
const VALUE = 500;
const RESPAWN_DELAY_MIN = 5;
const RESPAWN_DELAY_MAX = 9;
// A pellet won't respawn until the cluster has moved at least one full
// screen-length away from its host shard — the player has to literally
// travel to the other side of the screen for the ring to refill.
// Camping one shard and grazing forever is out by construction.
// Orbit band around each shard, expressed as multiples of the shard's
// own radius. Inner edge sits just outside the shard hull; outer edge
// gives the ring some thickness so multiple pellets don't share a path.
const ORBIT_BAND_MIN = 1.45;
const ORBIT_BAND_MAX = 2.15;
// Orbital angular speed, rad/s. Slow enough that a careful player can
// time absorptions; fast enough that the rings visibly rotate.
const ORBIT_SPEED_MIN = 0.10;
const ORBIT_SPEED_MAX = 0.32;
const WOBBLE_AMP_MIN = 4;
const WOBBLE_AMP_MAX = 10;
const WOBBLE_FREQ_MIN = 0.6;
const WOBBLE_FREQ_MAX = 1.4;
const PULSE_AMP = 0.18;
const PULSE_FREQ_MIN = 1.0;
const PULSE_FREQ_MAX = 2.2;

export class NutrientField {
  private parent: Container;
  private obstacles: ReadonlyArray<Obstacle>;
  private nutrients: Nutrient[] = [];
  private time = 0;

  constructor(parent: Container, obstacles: ReadonlyArray<Obstacle>, count: number) {
    this.parent = parent;
    this.obstacles = obstacles;
    if (obstacles.length === 0) return;
    let seed = 0xC0FFEE;
    const rand = () => {
      seed = (seed * 1664525 + 1013904223) | 0;
      return ((seed >>> 0) % 100000) / 100000;
    };
    for (let i = 0; i < count; i++) {
      const g = new Graphics();
      this.parent.addChild(g);
      const obstacleIdx = i % this.obstacles.length;
      const n = this.makeNutrient(obstacleIdx, rand, g);
      // Mix of CW/CCW so neighbouring shards don't all rotate the same
      // way — looks chaotic, less like a clock face.
      if (rand() < 0.5) n.orbitSpeed = -n.orbitSpeed;
      this.applyOrbit(n);
      this.draw(n);
      this.nutrients.push(n);
    }
  }

  private makeNutrient(obstacleIdx: number, rand: () => number, g: Graphics): Nutrient {
    const obs = this.obstacles[obstacleIdx];
    const band = ORBIT_BAND_MIN + rand() * (ORBIT_BAND_MAX - ORBIT_BAND_MIN);
    return {
      x: 0, y: 0,
      obstacle: obstacleIdx,
      orbitAngle: rand() * Math.PI * 2,
      orbitSpeed: ORBIT_SPEED_MIN + rand() * (ORBIT_SPEED_MAX - ORBIT_SPEED_MIN),
      orbitRadius: obs.radius * band,
      wobbleAmp: WOBBLE_AMP_MIN + rand() * (WOBBLE_AMP_MAX - WOBBLE_AMP_MIN),
      wobblePhase: rand() * Math.PI * 2,
      wobbleFreq: WOBBLE_FREQ_MIN + rand() * (WOBBLE_FREQ_MAX - WOBBLE_FREQ_MIN),
      pulsePhase: rand() * Math.PI * 2,
      pulseFreq: PULSE_FREQ_MIN + rand() * (PULSE_FREQ_MAX - PULSE_FREQ_MIN),
      alive: true,
      respawnIn: 0,
      g,
    };
  }

  private applyOrbit(n: Nutrient): void {
    const obs = this.obstacles[n.obstacle];
    const r = n.orbitRadius + Math.sin(this.time * n.wobbleFreq + n.wobblePhase) * n.wobbleAmp;
    n.x = obs.cx + Math.cos(n.orbitAngle) * r;
    n.y = obs.cy + Math.sin(n.orbitAngle) * r;
  }

  private draw(n: Nutrient): void {
    n.g.clear();
    if (!n.alive) return;
    n.g.position.set(n.x, n.y);
    n.g.circle(0, 0, RADIUS * 2.4).fill({ color: 0x4FD1C5, alpha: 0.10 });
    n.g.circle(0, 0, RADIUS * 1.6).fill({ color: 0x4FD1C5, alpha: 0.22 });
    n.g.circle(0, 0, RADIUS).fill({ color: 0xC4FAF0 });
  }

  update(dt: number, clusterX: number, clusterY: number, screenW: number, screenH: number): void {
    this.time += dt;
    const t = this.time;
    // "Other side of the screen" gate — once the cluster has travelled
    // at least the longer screen dimension away from a shard, the shard
    // is solidly off-screen and a return trip feels like a fresh visit.
    const clear = Math.max(screenW, screenH);
    const clearSq = clear * clear;
    for (const n of this.nutrients) {
      if (!n.alive) {
        n.respawnIn -= dt;
        if (n.respawnIn <= 0) {
          const obs = this.obstacles[n.obstacle];
          const dx = clusterX - obs.cx;
          const dy = clusterY - obs.cy;
          if (dx * dx + dy * dy >= clearSq) {
            // Respawn at a random angle on the same shard's ring so the
            // pellet doesn't always reappear where it was eaten.
            n.orbitAngle = Math.random() * Math.PI * 2;
            n.alive = true;
            this.applyOrbit(n);
            this.draw(n);
          }
        }
        continue;
      }
      n.orbitAngle += n.orbitSpeed * dt;
      this.applyOrbit(n);

      n.g.position.set(n.x, n.y);
      const s = 1 + Math.sin(t * n.pulseFreq + n.pulsePhase) * PULSE_AMP;
      n.g.scale.set(s);
    }
  }

  /**
   * Absorb every nutrient inside (cx, cy) ± r. Returns the total
   * biomass gained, the number of pellets eaten, and their positions
   * (so callers can emit per-hit FX).
   */
  absorbAt(cx: number, cy: number, r: number): { biomass: number; count: number; hits: { x: number; y: number }[] } {
    let biomass = 0;
    let count = 0;
    const hits: { x: number; y: number }[] = [];
    const reach = r + RADIUS;
    const reachSq = reach * reach;
    for (const n of this.nutrients) {
      if (!n.alive) continue;
      const dx = n.x - cx;
      const dy = n.y - cy;
      if (dx * dx + dy * dy <= reachSq) {
        biomass += VALUE;
        count++;
        hits.push({ x: n.x, y: n.y });
        n.alive = false;
        n.respawnIn = RESPAWN_DELAY_MIN + Math.random() * (RESPAWN_DELAY_MAX - RESPAWN_DELAY_MIN);
        n.g.clear();
      }
    }
    return { biomass, count, hits };
  }

  destroy(): void {
    for (const n of this.nutrients) n.g.destroy();
    this.nutrients = [];
  }
}
